<template>
  <Loader v-if="loading" :inverse="true"></Loader>
  <template v-else>
    <div class="step-body">
      <p class="step-title">Amount Requested</p>
      <p class="step-desc">Input the amount you would like to borrow.</p>
      <div class="body-content">
        <div class="form-group values-group">
          <div class="value-display">
            <label>Total Line Amount:</label>
            <p class="value">{{ loanAmount }}</p>
          </div>
          <div class="value-display">
            <label>Available to Borrow:</label>
            <p class="value">{{ availableToBorrow }}</p>
          </div>
          <div class="value-display">
            <label>Last Requested Draw:</label>
            <p class="value">{{ lastRequestedDraw }}</p>
          </div>
        </div>
        <div class="form-group requested-amount">
          <label for="requestedAmount">Requested Amount</label>
          <base-form-field
            :schema="schema.amount"
            v-model="requestedAmount"
            :compare="{ availableToBorrow: Loan?.availableToBorrow, minimum, minimumDisplay }"
          />
        </div>
      </div>
    </div>
  </template>
  <template v-if="!loading">
    <div class="step-btns">
      <button
        type="button"
        class="btn btn-secondary"
        :disabled="loading"
        @click.prevent="$emit('goback')"
      >
        Go Back
      </button>
      <button
        type="button"
        class="btn btn-primary btn-sm cuscd-button-color-cta"
        :disabled="loading || requestedAmount === '' || !validRequestedAmount"
        @click.prevent="completeRequestAmount"
      >
        Review Request
      </button>
    </div>
  </template>
</template>

<script lang="ts">
import { defineComponent, reactive, computed } from "vue";
import { useStore, mapGetters } from "vuex";
import useFormatter from "@/use/formatter";
import useFormHelpers from "@/use/formHelpers";
import useValidation from "@/use/validation";
import Loader from "@/components/shared/Loader.vue";
import { Disbursement } from "@/models";
import { FormSchema } from "@/models/form";
import BaseFormField from "../shared/BaseFormField.vue";

export default defineComponent({
  components: { Loader, BaseFormField },
  props: {
    loan: {
      type: Object,
      required: true,
    },
  },
  emits: ["complete", "goback", "close", "update:modelValue"],
  setup(props) {
    const { formatMoney, formatCurrency } = useFormatter();
    const { validate, validateForm } = useValidation();
    const { months, years } = useFormHelpers();
    const store = useStore();

    const PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT = computed(
      () => store.getters["PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT"],
    );
    const PROGRAM_MINIMUM_SUBSEQUENT_DRAW_AMOUNT = computed(
      () => store.getters["PROGRAM_MINIMUM_SUBSEQUENT_DRAW_AMOUNT"],
    );
    const PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT_DISPLAY = computed(
      () => store.getters["PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT_DISPLAY"],
    );
    const PROGRAM_MINIMUM_SUBSEQUENT_DRAW_AMOUNT_DISPLAY = computed(
      () => store.getters["PROGRAM_MINIMUM_SUBSEQUENT_DRAW_AMOUNT_DISPLAY"],
    );

    const isSubsequentDraw = computed<boolean>(() => {
      const isLastDrawGreaterThanZero =
        (props.loan?.draws?.[props.loan?.draws?.length - 1]?.requestedAmount || 0) > 0;

      if (
        props.loan?.draws === undefined ||
        props.loan?.draws === null ||
        props.loan?.draws?.length === 0 ||
        (props?.loan?.draws?.length > 0 && !isLastDrawGreaterThanZero)
      ) {
        return false;
      }

      return true;
    });

    const minimum = computed(() => {
      if (!isSubsequentDraw.value) {
        return PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT.value;
      }

      return PROGRAM_MINIMUM_SUBSEQUENT_DRAW_AMOUNT.value;
    });

    const minimumDisplay = computed(() => {
      if (!isSubsequentDraw.value) {
        return PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT_DISPLAY.value;
      }

      return PROGRAM_MINIMUM_SUBSEQUENT_DRAW_AMOUNT_DISPLAY.value;
    });

    let schema = reactive({
      amount: {
        name: "amount",
        type: "money",
        subtype: "drawAmount",
        hideLabel: true,
        required: true,
        allowZero: false,
        allowDecimals: false,
        errors: [] as string[],
        minimum: minimum.value,
      },
    }) as FormSchema;

    let errors = reactive([]) as string[];

    function isNumberKey(evt: any) {
      const charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode < 48 || charCode > 57) return false;

      return true;
    }

    return {
      errors,
      schema,
      store,
      validate,
      validateForm,
      months,
      years,
      formatMoney,
      formatCurrency,
      isNumberKey,
      isSubsequentDraw,
      minimum,
      minimumDisplay,
    };
  },
  data: () => ({
    loading: true,
    requestedAmount: "",
    amountError: "",
  }),
  computed: {
    ...mapGetters("fundsModule", [
      "SchoolName",
      "AcademicPeriod",
      "AmountRequested",
      "ApprovedLoanAmount",
      "Loan",
      "EnrollmentStatusName",
    ]),
    availableToBorrow(): string {
      let number = 0;
      if (this.Loan?.availableToBorrow) {
        number = this.Loan?.availableToBorrow;
      }
      return this.formatMoney(number);
    },
    loanAmount(): string {
      let number = 0;
      if (this.Loan?.loanAmount) {
        number = this.Loan?.loanAmount;
      }
      return this.formatMoney(number);
    },
    lastRequestedDraw(): string {
      let number = 0;

      if (this.Loan?.draws) {
        number = this.Loan?.draws[this.Loan?.draws.length - 1 || 0]?.requestedAmount || 0;
      }

      return this.formatMoney(number);
    },
    validRequestedAmount(): boolean {
      let valid = true;
      const amount = Number(this.requestedAmount.replace(/,/g, ""));
      const availableToBorrowToInt = parseInt(
        this.availableToBorrow.replace(/,/g, "").replace("$", ""),
      );

      // 1. Validate that the amount is a number
      if (isNaN(amount)) {
        valid = false;
      }
      // 2. Validate that the amount is in whole dollars, no cents
      else if (amount % 1 !== 0) {
        valid = false;
      }
      // Validate that the requested amount meets minimum $1000 for initial draws and $250 for subsequent draws
      if (this.requestedAmount !== "" && amount < this.minimum) {
        valid = false;
      }
      // 3. Validate the requested amount is below available to borrow amount
      else if (availableToBorrowToInt && amount > availableToBorrowToInt) {
        valid = false;
      }

      return valid;
    },
  },
  methods: {
    completeRequestAmount() {
      this.store.commit("fundsModule/resetDisbursement");
      const terms = this.AcademicPeriod.split("-");
      const cleanRequestAmount = this.requestedAmount.replace(/,/g, "");
      const requestedAmount = parseInt(cleanRequestAmount);
      terms.forEach((term: any) => {
        const disburse: Disbursement = {
          schoolName: this.SchoolName,
          enrollmentTerm: term.trim(),
          requestedAmount: `${new Intl.NumberFormat("en-US", {
						style: "currency",
						currency: "USD",
					}).format(
            requestedAmount / terms.length,
          )}`,
          enrollmentStatusName: this.EnrollmentStatusName,
        };
        this.store.commit("fundsModule/setRequestAmount", cleanRequestAmount);
        this.store.commit("fundsModule/setDisbursement", disburse);
      });

      this.$emit("complete", { step: 4 });
      this.loading = true;
    },
  },
  mounted() {
    this.loading = false;

    //Check if there was a previous set amount..
    this.requestedAmount = this.AmountRequested;
  },
});
</script>
